// src/prototype/components/RelatedViewsToolbar.tsx
// ============================================================
// RELATED VIEWS TOOLBAR
// Shared navigation toolbar for drilldown pages
// Matches the "Evaluation Tools" strip styling from Step 2
// 
// Used on:
// - Use Case Profile (/opportunity/:id/brief)
// - Scoring Rationale (/opportunity/:id/rationale)
// - ROI Model (/opportunity/:id/roi-model)
//
// UPDATED March 2026: Added backState prop so callers can pass
// additional state (e.g. returnToTab) into the back navigation
// without overriding restore scroll state.
// ============================================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Target, 
  LineChart,
  ArrowRight,
} from 'lucide-react';

export interface RestoreState {
  scrollY: number;
  useCaseId: string;
  expanded?: boolean;
  from?: string;
}

export interface RelatedViewsToolbarProps {
  useCaseId: string;
  currentView: 'profile' | 'rationale' | 'roi';
  restoreState?: RestoreState;
  backLabel?: string;
  backPath?: string;
  /** Additional state to merge into the back navigation (e.g. { returnToTab: 'explore' }) */
  backState?: Record<string, unknown>;
}

export const RelatedViewsToolbar: React.FC<RelatedViewsToolbarProps> = ({
  useCaseId,
  currentView,
  restoreState,
  backLabel = 'Back to Evaluate & Shortlist',
  backPath = '/opportunities',
  backState,
}) => {
  const navigate = useNavigate();

  const views = [
    { 
      key: 'profile' as const,
      icon: FileText, 
      label: 'Use Case Profile', 
      path: `/opportunity/${useCaseId}/brief`,
    },
    { 
      key: 'rationale' as const,
      icon: Target, 
      label: 'Scoring Rationale', 
      path: `/opportunity/${useCaseId}/rationale`,
    },
    { 
      key: 'roi' as const,
      icon: LineChart, 
      label: 'ROI Model', 
      path: `/opportunity/${useCaseId}/roi-model`,
    },
  ];

  const handleBack = () => {
    // Merge restore state with any additional caller-supplied state
    navigate(backPath, {
      state: {
        restore: restoreState,
        ...backState,
      },
    });
  };

  const handleViewChange = (view: typeof views[0]) => {
    if (view.key === currentView) return;
    
    // Pass restore state forward so it's preserved when switching views
    navigate(view.path, { state: { restore: restoreState } });
  };

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] p-4 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Back Navigation */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm font-medium text-[#717182] hover:text-[#18365E] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {backLabel}
        </button>

        {/* Related Views Buttons */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mr-2">
            Related Views
          </span>
          <div className="flex gap-2">
            {views.map((view) => {
              const Icon = view.icon;
              const isActive = view.key === currentView;
              
              return (
                <button
                  key={view.key}
                  onClick={() => handleViewChange(view)}
                  disabled={isActive}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                    isActive
                      ? 'bg-[#18365E]/10 text-[#18365E] border border-[#18365E]/30 cursor-default'
                      : 'bg-white text-[#717182] border border-[#e5e7eb] hover:border-[#18365E] hover:text-[#18365E] hover:bg-[#18365E]/5'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {view.label}
                  {!isActive && <ArrowRight className="w-3 h-3 opacity-50" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatedViewsToolbar;